import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import {
  enumLoadStatus,
  enumSalesLoadLineItemPhase,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import { Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { loadMarkAsReceivedAtom } from '../../../_graphql/mutationLoadMarkAsReceived';
import { loadMarkAsShippedAtom } from '../../../_graphql/mutationLoadMarkAsShipped';
import { loadMarkAsVoidedAtom } from '../../../_graphql/mutationLoadMarkAsVoided';
import { loadReconcileAtom } from '../../../_graphql/mutationLoadReconcile';
import { loadRevertStatusAtom } from '../../../_graphql/mutationLoadRevertStatus';
import { useLoadChangesSubscription } from '../../../_graphql/subscriptionLoadChanges';
import { $cleanupMaterials } from '../../../_state/materialsForm.state';
import {
  $cleanupOutboundCustomerForm,
  $flushOutboundCustomerAutoSave,
  $syncOutboundCustomerForm,
} from '../../../_state/scaleTicketOutboundCustomerForm.state';
import {
  $cleanupOutboundDetailsForm,
  $flushOutboundDetailsAutoSave,
  $syncOutboundDetailsForm,
} from '../../../_state/scaleTicketOutboundInfoForm.state';
import {
  $saveOutboundMaterials,
  $syncOutboundMaterialsForm,
} from '../../../_state/scaleTicketOutboundMaterialsForm.state';
import {
  $cleanupOutboundNotesForm,
  $flushOutboundNotesAutoSave,
  $syncOutboundNotesForm,
} from '../../../_state/scaleTicketOutboundNotesForm.state';
import {
  $outboundTicketDetail,
  $scaleTicketId,
} from '../../../_state/scaleTicketPro.state';
import { tradeRoutePaths as tradeRoutes } from '../../../routes';
import {
  $itemsInterfaceEntries,
  $purchaseSummary,
} from '../../_shared/ItemsInterface/_state';
import { ShipTicketDialog } from '../../_shared/ItemsInterface/dialogs/ShipTicketDialog';
import { useOutboundPrintDocuments } from '../hooks/useOutboundPrintDocuments';
import { useUStudioTicketController } from '../hooks/useUStudioTicketController';
import { UStudioScaleTicketController } from '../shared/UStudioScaleTicketController';

import { OutboundTicketHeader } from './OutboundTicketHeader';
import { OutboundTicketTabs } from './OutboundTicketTabs';

import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import { $headerRect } from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { transformLoadLineItemsToReceivedLineItems } from '~/modules/trade/utils/load';
import { type FocusPosition } from '~/shared/components/FieldFocusIndicator';
import { useHeaderRectMeasurement } from '~/shared/hooks/useHeaderRectMeasurement';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';
import { usePresence } from '~/shared/providers/presence';

const RECONCILIATION_ELIGIBLE_STATUSES: LoadStatus[] = [
  enumLoadStatus.Shipped,
  enumLoadStatus.Received,
  enumLoadStatus.Reconciled,
];

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET DETAIL ⚛️ ⚛️ ⚛️
//
export const OutboundTicketDetail = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Refs
  const prevTicketIdRef = React.useRef<string | undefined>(undefined);

  // States
  const [isShipDialogOpen, setIsShipDialogOpen] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);

  // Atoms
  const ticketId = useAtomValue($scaleTicketId);
  const { data, loadingState, refetch } = useAtomValue($outboundTicketDetail);
  const syncDetailsForm = useSetAtom($syncOutboundDetailsForm);
  const syncCustomerForm = useSetAtom($syncOutboundCustomerForm);
  const syncNotesForm = useSetAtom($syncOutboundNotesForm);
  const syncMaterialsForm = useSetAtom($syncOutboundMaterialsForm);
  const flushDetailsAutoSave = useSetAtom($flushOutboundDetailsAutoSave);
  const flushCustomerAutoSave = useSetAtom($flushOutboundCustomerAutoSave);
  const flushNotesAutoSave = useSetAtom($flushOutboundNotesAutoSave);
  const saveOutboundMaterials = useSetAtom($saveOutboundMaterials);
  const cleanupDetailsForm = useSetAtom($cleanupOutboundDetailsForm);
  const cleanupCustomerForm = useSetAtom($cleanupOutboundCustomerForm);
  const cleanupNotesForm = useSetAtom($cleanupOutboundNotesForm);
  const cleanupOutboundMaterials = useSetAtom($cleanupMaterials);
  const itemsInterfaceEntries = useAtomValue($itemsInterfaceEntries);
  const purchaseSummary = useAtomValue($purchaseSummary);
  const setHeaderRect = useSetAtom($headerRect);
  const shipMutation = useSetAtom(loadMarkAsShippedAtom({}));
  const receiveMutation = useSetAtom(loadMarkAsReceivedAtom({}));
  const voidMutation = useSetAtom(loadMarkAsVoidedAtom({}));
  const reconcileMutation = useSetAtom(loadReconcileAtom({}));
  const revertMutation = useSetAtom(loadRevertStatusAtom({}));
  const focusedField = useAtomValue($focusedField);
  const setRemotePositions = useSetAtom($remoteFocusPositions);

  const presenceRoom = ticketId ? `ticket-fields:${ticketId}` : '';
  const {
    members: presenceMembers,
    announce: presenceAnnounce,
    isSelf: presenceIsSelf,
  } = usePresence(presenceRoom, { join: !!ticketId });

  const presenceAnnounceRef = React.useRef(presenceAnnounce);
  presenceAnnounceRef.current = presenceAnnounce;

  const outboundTicketSubscriptionIds = React.useMemo(
    () => (ticketId && ticketId !== 'new' ? [ticketId] : []),
    [ticketId],
  );

  // Other data hooks
  const headerRef = useHeaderRectMeasurement(setHeaderRect);
  const toast = useToast();

  const ustudio = useUStudioTicketController({
    ticketId: ticketId ?? null,
    currencyCode: data?.load?.currencyCode ?? 'USD',
    saveMaterials: saveOutboundMaterials,
    failedToSaveMessage: t.scaleTicketPro.materialsTab.failedToSaveMaterials,
  });

  const lastKnownLoadVersionRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (data?.load?.version != null) {
      lastKnownLoadVersionRef.current = data.load.version;
    }
  }, [data?.load?.version]);

  useLoadChangesSubscription(
    { ids: outboundTicketSubscriptionIds },
    {
      onEvent: (signal) => {
        if (!signal || signal.version === lastKnownLoadVersionRef.current)
          return;
        refetch?.();
      },
    },
  );

  // Memoized values
  const ticket = data?.load ?? null;
  const status = ticket?.status as LoadStatus | undefined;
  const isLoading = !!loadingState?.isInitialLoading;
  const showReconciliation =
    !!status && RECONCILIATION_ELIGIBLE_STATUSES.includes(status);
  const printDocuments = useOutboundPrintDocuments(ticket);

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(tradeRoutes.scaleTickets);
  }, [navigate]);

  const handleRefetch = React.useCallback(() => {
    refetch?.();
  }, [refetch]);

  const handleShip = React.useCallback(async () => {
    if (!ticketId || !ticket) return;
    try {
      setIsProcessing(true);
      saveOutboundMaterials({
        ticketId,
        immediate: true,
        failedToSaveMessage:
          t.scaleTicketPro.materialsTab.failedToSaveMaterials,
      });
      await shipMutation({
        loadId: ticketId,
        shippedDate: new Date().toISOString(),
        lightWeight: itemsInterfaceEntries.lightWeight ?? null,
        heavyWeight: itemsInterfaceEntries.heavyWeight ?? null,
      });
      toast.success(t.scaleTicketPro.outboundDetail.toast.shipSuccess);
      setIsShipDialogOpen(false);
      handleRefetch();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.outboundDetail.toast.shipError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [
    ticketId,
    ticket,
    saveOutboundMaterials,
    itemsInterfaceEntries,
    shipMutation,
    toast,
    handleRefetch,
    t,
  ]);

  const handleVoid = React.useCallback(
    async (voidReason?: string) => {
      if (!ticketId) return;
      try {
        setIsProcessing(true);
        await voidMutation({ loadId: ticketId, voidReason });
        toast.success(t.scaleTicketPro.outboundDetail.toast.voidSuccess);
        handleRefetch();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : t.scaleTicketPro.outboundDetail.toast.voidError,
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [ticketId, voidMutation, toast, handleRefetch, t],
  );

  const handleRevert = React.useCallback(
    async (targetStatus: LoadStatus) => {
      if (!ticketId) return;
      try {
        setIsProcessing(true);
        await revertMutation({ id: ticketId, targetStatus });
        toast.success(t.scaleTicketPro.outboundDetail.toast.revertSuccess);
        handleRefetch();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : t.scaleTicketPro.outboundDetail.toast.revertError,
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [ticketId, revertMutation, toast, handleRefetch, t],
  );

  const handleReceive = React.useCallback(
    async (receivedDate: CalendarDate) => {
      if (!ticketId || !ticket) return;
      try {
        setIsProcessing(true);
        const shippedLineItems =
          ticket.lineItems?.filter(
            (li) => li.phase === enumSalesLoadLineItemPhase.Shipped,
          ) ?? [];
        const receivedLineItems =
          transformLoadLineItemsToReceivedLineItems(shippedLineItems);
        await receiveMutation({
          loadId: ticketId,
          receivedDate,
          receivedLineItems,
        });
        toast.success(t.scaleTicketPro.outboundDetail.toast.receiveSuccess);
        handleRefetch();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : t.scaleTicketPro.outboundDetail.toast.receiveError,
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [ticketId, ticket, receiveMutation, toast, handleRefetch, t],
  );

  const handleReconcile = React.useCallback(async () => {
    if (!ticketId) return;
    try {
      setIsProcessing(true);
      await reconcileMutation({ id: ticketId });
      toast.success(t.scaleTicketPro.outboundDetail.toast.reconcileSuccess);
      handleRefetch();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.scaleTicketPro.outboundDetail.toast.reconcileError,
      );
    } finally {
      setIsProcessing(false);
    }
  }, [ticketId, reconcileMutation, toast, handleRefetch, t]);

  // Effects
  React.useEffect(() => {
    presenceAnnounceRef.current({ focusedField });
  }, [focusedField]);

  React.useEffect(() => {
    const positions: FocusPosition[] = [];
    for (const [id, member] of presenceMembers) {
      if (presenceIsSelf(id)) continue;
      const meta = member.metadata as Record<string, unknown> | null;
      const field = meta?.focusedField;
      if (typeof field === 'string') {
        positions.push({
          userId: id,
          fieldId: field,
          name: member.name ?? undefined,
        });
      }
    }
    setRemotePositions(positions);
  }, [presenceMembers, presenceIsSelf, setRemotePositions]);

  /** Flush pending auto-saves and cleanup state when navigating away from a ticket */
  React.useEffect(() => {
    const prevId = prevTicketIdRef.current;
    prevTicketIdRef.current = ticketId;
    if (prevId && prevId !== ticketId) {
      flushDetailsAutoSave(prevId);
      flushCustomerAutoSave(prevId);
      flushNotesAutoSave(prevId);
      saveOutboundMaterials({
        ticketId: prevId,
        immediate: true,
        failedToSaveMessage:
          t.scaleTicketPro.materialsTab.failedToSaveMaterials,
      });
      cleanupDetailsForm(prevId);
      cleanupCustomerForm(prevId);
      cleanupNotesForm(prevId);
      cleanupOutboundMaterials(prevId);
    }
  }, [
    ticketId,
    flushDetailsAutoSave,
    flushCustomerAutoSave,
    flushNotesAutoSave,
    saveOutboundMaterials,
    cleanupDetailsForm,
    cleanupCustomerForm,
    cleanupNotesForm,
    cleanupOutboundMaterials,
    t,
  ]);

  /** Sync all form state when ticket data changes */
  React.useLayoutEffect(() => {
    if (!ticket) return;
    const serverUpdatedAt = ticket.timestamps.updatedAt?.toString();
    syncDetailsForm({
      ticketId: ticket.id,
      ticket: {
        description: ticket.description ?? null,
        truckNumber: ticket.truckNumber ?? null,
        trailerNumber: ticket.trailerNumber ?? null,
        driverName: ticket.driverName ?? null,
        containerNumber: ticket.containerNumber ?? null,
        sealNumber: ticket.sealNumber ?? null,
        bolNumber: ticket.bolNumber ?? null,
        deliveryNumber: ticket.deliveryNumber ?? null,
        releaseNumber: ticket.releaseNumber ?? null,
      },
      serverUpdatedAt,
    });
    syncCustomerForm({
      ticketId: ticket.id,
      ticket: {
        facilityId: ticket.facilityId ?? null,
        customerAccountId: ticket.customerAccountId ?? null,
        customerAccountAddressId: ticket.customerAccountAddressId ?? null,
        salesOrderId: ticket.salesOrderId ?? null,
        receivingFacilityId: ticket.receivingFacilityId ?? null,
        carrierAccountId: ticket.carrierAccountId ?? null,
        freightTerms: ticket.freightTerms ?? null,
        shippingCarrierName: ticket.shippingCarrierName ?? null,
        scac: ticket.scac ?? null,
      },
      serverUpdatedAt,
    });
    syncNotesForm({
      ticketId: ticket.id,
      ticket,
      serverUpdatedAt,
    });
    syncMaterialsForm({
      ticketId: ticket.id,
      documentNumber: Number(ticket.loadNumber),
      lineItems: ticket.lineItems ?? [],
      deductions: ticket.deductions ?? [],
      freight: {
        freightCost: null,
        freightTerms: ticket.freightTerms ?? null,
      },
      serverUpdatedAt,
    });
  }, [
    ticket,
    syncDetailsForm,
    syncCustomerForm,
    syncNotesForm,
    syncMaterialsForm,
  ]);

  if (!ticketId) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-foreground/50">
          {t.scaleTicketPro.outboundDetail.noLoadSelected}
        </div>
      </div>
    );
  }

  if (!ticket && !isLoading) {
    return (
      <Row xCenter yCenter className="h-full w-full">
        <div className="text-failure">
          {t.scaleTicketPro.outboundDetail.loadNotFound}
        </div>
      </Row>
    );
  }

  if (!ticket) {
    return (
      <Row xCenter yCenter className="h-full w-full">
        <div className="text-neutral/50">
          {t.scaleTicketPro.outboundDetail.noLoadSelected}
        </div>
      </Row>
    );
  }

  return (
    <div className="@OutboundTicketDetail flex h-full w-full min-w-0 flex-col gap-2">
      <UStudioScaleTicketController
        {...ustudio}
        onHold={() => undefined}
        onRelease={() => undefined}
        onFinish={() => setIsShipDialogOpen(true)}
        ticketStatus={ticket.status}
        isHeld={false}
      />
      <div className="flex min-h-0 flex-1 flex-col" ref={headerRef}>
        <OutboundTicketTabs
          ticket={ticket}
          isLoading={isLoading}
          header={
            <OutboundTicketHeader
              ticket={ticket}
              status={status}
              materialCount={ticket.lineItems?.length}
              onShip={() => setIsShipDialogOpen(true)}
              onVoid={handleVoid}
              onRevert={handleRevert}
              onReceive={handleReceive}
              onReconcile={handleReconcile}
              isProcessing={isProcessing}
              reviewed={ticket.reviewed}
              showReconciliation={showReconciliation}
              onGoBack={handleBack}
              printDocuments={printDocuments}
            />
          }
        />
      </div>

      <ShipTicketDialog
        isOpen={isShipDialogOpen}
        onOpenChange={setIsShipDialogOpen}
        load={ticket}
        onConfirm={handleShip}
        isProcessing={isProcessing}
        customerName={ticket.customerName}
        subtotal={purchaseSummary.subtotal}
        deductionsTotal={purchaseSummary.deductionsTotal}
        grandTotal={purchaseSummary.grandTotal}
      />
    </div>
  );
};
